import { useState, useRef, useCallback, useContext } from 'react'
import { useSnackbar } from 'notistack'
import Context from '../../contexts/Context'
import { getToken } from '../../services/Main/AuthToken'
import { getCondoId } from '../../services/Main/AuthStorage'
import FloatingChatButton from './FloatingChatButton'
import ChatPanel from './ChatPanel'
import ChatHeader from './ChatHeader'
import ChatMessages from './ChatMessages'
import SuggestedQuestions from './SuggestedQuestions'
import ChatInput from './ChatInput'

const API_URL = process.env.REACT_APP_API_URL || 'https://api.blocob3.com.br/'

const AIAssistant = () => {
  const { user } = useContext(Context)
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef(null)
  const { enqueueSnackbar } = useSnackbar()

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return

    const userMessage = { id: Date.now(), role: 'user', content: text, timestamp: new Date() }
    const assistantMessage = { id: Date.now() + 1, role: 'assistant', content: '', timestamp: new Date() }

    setMessages(prev => [...prev, userMessage, assistantMessage])
    setInputValue('')
    setIsLoading(true)

    abortControllerRef.current = new AbortController()

    try {
      const token = getToken()
      const response = await fetch(`${API_URL}/ai/chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: text,
          conversation_history: messages.map(m => ({ role: m.role, content: m.content })),
          condo_id: getCondoId(),
        }),
        signal: abortControllerRef.current.signal,
      })

      if (response.status === 429) {
        throw new Error('RATE_LIMIT')
      }

      if (!response.ok) throw new Error(`Erro: ${response.status}`)

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') break
            try {
              const parsed = JSON.parse(data)
              if (parsed.error) {
                enqueueSnackbar(parsed.error, { variant: 'error' })
                setMessages(prev => prev.filter(m => m.content !== ''))
                return
              }
              setMessages(prev => {
                const updated = [...prev]
                const lastMsg = updated[updated.length - 1]
                return [...updated.slice(0, -1), { ...lastMsg, content: lastMsg.content + (parsed.content || '') }]
              })
            } catch {
              setMessages(prev => {
                const updated = [...prev]
                const lastMsg = updated[updated.length - 1]
                return [...updated.slice(0, -1), { ...lastMsg, content: lastMsg.content + data }]
              })
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const errorMessage = err.message === 'RATE_LIMIT'
          ? 'Limite de perguntas atingido. Tente novamente em alguns minutos.'
          : 'Erro ao processar sua mensagem. Tente novamente.'
        enqueueSnackbar(errorMessage, { variant: 'error' })
        setMessages(prev => prev.filter(m => m.content !== ''))
      }
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, messages, enqueueSnackbar])

  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    setIsLoading(false)
  }, [])

  const clearHistory = useCallback(() => {
    setMessages([])
  }, [])

  const handleSend = useCallback(() => {
    sendMessage(inputValue)
  }, [inputValue, sendMessage])

  return (
    <>
      {!open && <FloatingChatButton onClick={() => setOpen(true)} />}
      <ChatPanel open={open} onClose={() => setOpen(false)}>
        <ChatHeader
          onClose={() => setOpen(false)}
          onClearHistory={clearHistory}
          hasMessages={messages.length > 0}
        />
        {messages.length === 0 ? (
          <SuggestedQuestions onSelect={sendMessage} />
        ) : (
          <ChatMessages messages={messages} isLoading={isLoading} user={user} />
        )}
        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSend}
          onCancel={handleCancel}
          isLoading={isLoading}
        />
      </ChatPanel>
    </>
  )
}

export default AIAssistant
